import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { openBookConsultation } from "../lib/bookConsultation";
import Logo from "./Logo";

const navItems = [
  { label: "Services", path: "/services" },
  { label: "Work", path: "/work" },
  { label: "Thinking", path: "/thinking" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="nav-bar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-black/5 shadow-[0_4px_30px_-12px_rgba(0,0,0,0.08)]"
          : "bg-white/55 backdrop-blur-xl backdrop-saturate-150 border-b border-white/40"
      }`}
    >
      <div className="container-xl flex items-center justify-between h-16 md:h-[72px]">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="dotConverse — home"
          className="flex items-center gap-2.5 group"
        >
          <span className="h-2 w-2 rounded-full bg-[#FFB000] shadow-[0_0_12px_#FFB000] group-hover:scale-125 transition-transform" />
          <Logo asLink={false} height={22} testId="nav-logo-img" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={`px-4 py-2 text-[13px] tracking-tight rounded-full transition-colors ${
                pathname === item.path || pathname.startsWith(item.path + "/")
                  ? "text-[#1D1D1F] bg-black/5"
                  : "text-[#1D1D1F]/70 hover:text-[#1D1D1F] hover:bg-black/[0.04]"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={() => openBookConsultation("navigation_desktop")}
            data-testid="nav-cta-consultation"
            className="btn-pill btn-primary-amber text-[13px] px-5 py-2.5"
          >
            Book a consultation
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((s) => !s)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/5 text-[#1D1D1F]"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-black/5">
          <div className="container-xl py-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                className="py-3 px-3 rounded-2xl text-[#1D1D1F]/85 hover:bg-black/5 hover:text-[#1D1D1F] text-lg"
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => openBookConsultation("navigation_mobile")}
              data-testid="nav-mobile-cta"
              className="btn-pill btn-primary-amber mt-3 w-full"
            >
              Book a consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
